import React, { useState } from 'react';
import AgendarEncontroModal from '@/components/AgendarEncontroModal';
import { createRoot } from 'react-dom/client';
import logoAllepets from "@/assets/logo-allepets.png";

declare global {
  interface Window {
    __AUTH_ID__?: number | null;
  }
}

type HostService = {
  id?: number | string | null;
  name: string;
  description?: string | null;
  price?: number | string | null;
  maxPets?: number | null;
  requiresVariant?: boolean;
  packages?: Array<{
    id: string;
    units: number;
    price: number;
    validityQuantity: number;
    validityUnit: "day" | "week" | "month";
    variantKey?: string | null;
  }>;
  modalities?: Array<{
    key: string;
    label: string;
    price?: number | string | null;
  }> | null;
};

type DisplayService = {
  id?: number | string | null;
  name: string;
  description?: string | null;
  price?: number | string | null;
  maxPets?: number | null;
  requiresVariant?: boolean;
  variantKey?: string | null;
  packagesCount?: number;
  ctaLabel: string;
  availabilityNote?: string | null;
};

type HostPayload = {
  publicCode: string;
  userId?: number | string | null;
  isVerified?: boolean;
  name: string;
  city?: string | null;
  neighborhood?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
  publicUrl: string;
  reservationUrl: string;
  chatPath?: string | null;
  qrCodeUrl: string;
  services: HostService[];
  gallery: string[];
  acceptedPets: Record<string, boolean>;
  includedBenefits: Record<string, boolean>;
  acceptedSizes?: string[];
  cancellationPolicy?: {
    tag?: string | null;
    badge?: string | null;
    detalhe?: string | null;
  } | null;
  academyCredentials?: Array<{
    name: string;
    description?: string | null;
    icon?: string | null;
    courseTitle?: string | null;
    isFormationBadge?: boolean;
    badgeCode?: string | null;
    badgeUrl?: string | null;
    certificateCode?: string | null;
    certificateUrl?: string | null;
    score?: number | null;
    issuedAt: string;
  }>;
  experience: Record<string, boolean | string | number | null>;
};

function HostPublicProfile() {
  const root = document.getElementById('host-public-profile-root');

  if (!root) return null;

  const payload = JSON.parse(root.dataset.host || '{}') as HostPayload;
  const host = payload;
  const [agendarAberto, setAgendarAberto] = useState(false);
  const isLoggedIn = Boolean(window.__AUTH_ID__);
  const chatHref = host.chatPath
    ? isLoggedIn
      ? host.chatPath
      : `/login?redirect=${encodeURIComponent(host.chatPath)}`
    : '/login';

  const acceptedPetLabels: Record<string, string> = {
    caes: 'Aceita cães',
    gatos: 'Aceita gatos',
    outros: 'Aceita outros pets',
    filhotes: 'Aceita filhotes',
    idosos: 'Aceita pets idosos',
    necessidadesEspeciais: 'Aceita necessidades especiais',
  };

  const benefitLabels: Record<string, string> = {
    banho: 'Banho',
    passeio: 'Passeio',
    medicacao: 'Medicação',
    transporte: 'Transporte',
  };

  const experienceLabels: Record<string, string> = {
    tevePets: 'Já teve pets',
    cuidouPets: 'Já cuidou de pets',
    voluntarioOng: 'Voluntariado com animais',
    cuidadorProfissional: 'Cuidador profissional',
    auxiliarVeterinario: 'Auxiliar veterinário',
    negocioPet: 'Negócio pet',
    medicoVeterinario: 'Médico veterinário',
  };

  async function trackReservationClick() {
    try {
      await fetch(`/a/${host.publicCode}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          eventName: 'host_reservation_cta_clicked',
        }),
      });
    } catch {
      // Tracking não deve bloquear a reserva.
    }
  }

  const acceptedPetItems = Object.entries(payload.acceptedPets)
    .filter(([, value]) => Boolean(value))
    .map(([key]) => acceptedPetLabels[key] ?? key);

  const additionalServiceItems = Object.entries(payload.includedBenefits)
    .filter(([, value]) => Boolean(value))
    .map(([key]) => benefitLabels[key] ?? key);

  const displayServices: DisplayService[] = payload.services.flatMap((service): DisplayService[] => {
    if (service.requiresVariant && service.modalities?.length) {
      return service.modalities.map((modality) => ({
        id: service.id,
        name: modality.label,
        description: service.description,
        price: modality.price,
        maxPets: service.maxPets,
        requiresVariant: true,
        variantKey: modality.key,
        packagesCount: (service.packages || []).filter((pkg) => !pkg.variantKey || pkg.variantKey === modality.key).length,
        ctaLabel: 'Solicitar reserva',
        availabilityNote: 'Horários e disponibilidade variam conforme a modalidade escolhida.',
      }));
    }

    return [{
      id: service.id,
      name: service.name,
      description: service.description,
      price: service.price,
      maxPets: service.maxPets,
      requiresVariant: false,
      variantKey: null,
      packagesCount: (service.packages || []).length,
      ctaLabel: 'Solicitar reserva',
      availabilityNote: null,
    }];
  });

  const formatPrice = (value?: number | string | null) => {
    const numericValue =
      typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    return numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  return (
    <main className="min-h-screen bg-[#FFF8F6] text-slate-900">
      <section className="mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 lg:px-8 lg:pb-10">
        <header className="mb-6 flex items-center justify-between">
          <a href="/" className="flex-shrink-0 group">
            <img
              src={logoAllepets}
              alt="AllePets"
              className="transition-all duration-500 h-12 md:h-24 hover:scale-105 group-hover:drop-shadow-lg"
            />
          </a>

          <span className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand shadow-sm ring-1 ring-brand/10">
            {host.isVerified ? 'Perfil verificado' : 'Perfil público AllePets'}
          </span>
        </header>

        <div className="grid gap-6 lg:grid-cols-[1.4fr_0.8fr]">
          <section className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-brand/10">
            <div className="relative overflow-hidden bg-gradient-to-br from-brand via-brand-light to-warm px-6 py-10 text-white sm:px-10">
              <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/20" />
              <div className="absolute -bottom-20 right-20 h-52 w-52 rounded-full bg-success-light/30" />

              <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
                <div className="h-28 w-28 overflow-hidden rounded-3xl bg-white/20 ring-4 ring-white/50">
                  {host.photoUrl ? (
                    <img
                      src={host.photoUrl}
                      alt={host.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-4xl font-bold">
                      {host.name?.charAt(0)}
                    </div>
                  )}
                </div>

                <div>
                  <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-white/85">
                    Anfitrião ALLEPETS
                  </p>

                  <h1 className="text-3xl font-extrabold sm:text-5xl">
                    {host.name}
                  </h1>

                  {(host.neighborhood || host.city) && (
                    <p className="mt-3 text-lg font-medium text-white/90">
                      {[host.neighborhood, host.city].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-8 px-6 py-8 sm:px-10">
              <section>
                <h2 className="text-xl font-bold text-slate-950">
                  Sobre este anfitrião
                </h2>
                <p className="mt-3 leading-7 text-slate-600">
                  {host.bio ||
                    'Anfitrião cadastrado na ALLEPETS para cuidar do seu pet com segurança, carinho e acompanhamento pela plataforma.'}
                </p>
              </section>

              <section className="rounded-2xl border border-rose-100 bg-white p-5 shadow-sm lg:hidden">
                <h2 className="text-lg font-bold text-slate-950">Conhecer antes de reservar</h2>
                <p className="mt-2 text-sm text-slate-600">Agende gratuitamente um encontro de apresentação, sem compromisso.</p>
                <button type="button" onClick={() => window.__AUTH_ID__ ? setAgendarAberto(true) : window.location.assign('/login?redirect=' + encodeURIComponent(window.location.pathname))} className="mt-4 w-full rounded-xl bg-green-700 p-4 font-bold text-white">Agendar encontro gratuito</button>
              </section>

              {(payload.academyCredentials ?? []).length > 0 && (
                <section className="mt-8">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-bold text-slate-950">Formação e credenciais</h2>
                      <p className="mt-1 text-sm text-slate-600">
                        Conquistas verificáveis emitidas pela AllePets Academy.
                      </p>
                    </div>
                    <span className="rounded-full bg-[#EEF7DF] px-3 py-1 text-xs font-bold text-[#4F7F00]">
                      AllePets Academy
                    </span>
                  </div>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {(payload.academyCredentials ?? []).map((credential) => (
                      <article
                        key={credential.badgeCode || credential.certificateCode || credential.name}
                        className={credential.isFormationBadge
                          ? "rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm sm:col-span-2"
                          : "rounded-2xl border border-rose-100 bg-white p-5 shadow-sm"}
                      >
                        <div className="flex items-start gap-3">
                          <div className={credential.isFormationBadge
                            ? "flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-amber-100 text-3xl ring-4 ring-amber-50"
                            : "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FFF1F3] text-2xl"}>
                            {credential.icon || '🐾'}
                          </div>
                          <div className="min-w-0">
                            {credential.isFormationBadge ? (
                              <span className="mb-1 inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
                                Medalha de formação
                              </span>
                            ) : null}
                            <p className="font-bold text-slate-950">{credential.name}</p>
                            <p className="mt-1 text-sm text-slate-600">
                              {credential.isFormationBadge ? 'Formação completa AllePets Academy' : credential.courseTitle}
                            </p>
                          </div>
                        </div>

                        {credential.description ? (
                          <p className="mt-3 text-sm leading-6 text-slate-700">
                            {credential.description}
                          </p>
                        ) : null}

                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full bg-[#F7FBEF] px-2.5 py-1 text-xs font-bold text-[#4F7F00]">
                            Credencial verificada
                          </span>
                          {credential.score != null ? (
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                              Aproveitamento {credential.score}%
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          {credential.badgeUrl ? (
                            <a
                              href={credential.badgeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
                            >
                              Verificar AlleBadge
                            </a>
                          ) : null}
                          {credential.certificateUrl ? (
                            <a
                              href={credential.certificateUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center rounded-xl border border-brand px-4 py-2 text-sm font-bold text-brand transition hover:bg-brand hover:text-white"
                            >
                              Ver certificado
                            </a>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}

              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Serviços disponíveis</h2>

                {displayServices.length > 0 ? (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {displayServices.map((service: DisplayService) => (
                      <div
                        key={`${service.id ?? service.name}-${service.variantKey ?? 'default'}`}
                        className="rounded-2xl border border-rose-100 bg-white p-5 shadow-sm"
                      >
                        <h3 className="font-bold text-slate-950">{service.name}</h3>

                        {service.description && (
                          <p className="mt-2 text-sm leading-6 text-slate-700">
                            {service.description}
                          </p>
                        )}

                        {service.price ? (
                          <p className="mt-3 text-sm font-bold text-[#64A002]">
                            {service.requiresVariant
                              ? (formatPrice(service.price) ?? `R$ ${service.price}`)
                              : <>A partir de {formatPrice(service.price) ?? `R$ ${service.price}`}</>}
                          </p>
                        ) : null}

                        <p className="mt-3 text-sm font-semibold text-slate-700">
                          Aceita até:{' '}
                          {service.maxPets
                            ? `${service.maxPets} pet${service.maxPets === 1 ? '' : 's'}`
                            : 'sob consulta'}
                        </p>

                        {Number(service.packagesCount || 0) > 0 ? (
                          <span className="mt-3 inline-flex rounded-full bg-[#EEF7DF] px-2.5 py-1 text-xs font-bold text-[#4F7F00]">
                            Pacotes disponíveis
                          </span>
                        ) : null}

                        {service.availabilityNote ? (
                          <p className="mt-2 text-sm text-slate-600">
                            {service.availabilityNote}
                          </p>
                        ) : null}

                        {service.id ? (
                          <a
                            href={
                              service.variantKey
                                ? `/servico/${service.id}?modalidade=${encodeURIComponent(service.variantKey)}`
                                : `/servico/${service.id}`
                            }
                            onClick={() => {
                              void trackReservationClick();
                            }}
                            className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-[#64A002] px-4 py-3 text-sm font-bold text-white"
                          >
                            {service.ctaLabel}
                          </a>
                        ) : (
                          <button
                            type="button"
                            className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-slate-300 px-4 py-3 text-sm font-bold text-slate-700"
                            disabled
                          >
                            Solicitar reserva
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl bg-rose-50 p-5 text-sm text-slate-700">
                    Serviços disponíveis mediante solicitação pela ALLEPETS.
                  </div>
                )}
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Pets aceitos</h2>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {acceptedPetItems.length > 0 ? acceptedPetItems.map((item) => (
                      <div
                        key={item}
                        className="rounded-xl bg-[#F7FBEF] px-4 py-3 text-sm font-semibold text-slate-800"
                      >
                        {item}
                      </div>
                    )) : (
                      <div className="rounded-xl bg-[#F7FBEF] px-4 py-3 text-sm text-slate-700">
                        Não informado.
                      </div>
                    )}
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Porte</h2>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {(payload.acceptedSizes ?? []).length > 0 ? (payload.acceptedSizes ?? []).map((size) => (
                      <div
                        key={size}
                        className="rounded-xl border border-rose-100 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                      >
                        {size}
                      </div>
                    )) : (
                      <div className="rounded-xl border border-rose-100 bg-white px-4 py-3 text-sm text-slate-700">
                        Não informado.
                      </div>
                    )}
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Serviços adicionais</h2>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {additionalServiceItems.length > 0 ? additionalServiceItems.map((item) => (
                      <div
                        key={item}
                        className="rounded-xl bg-[#F7FBEF] px-4 py-3 text-sm font-semibold text-slate-800"
                      >
                        {item}
                      </div>
                    )) : (
                      <div className="rounded-xl bg-[#F7FBEF] px-4 py-3 text-sm text-slate-700">
                        Não informado.
                      </div>
                    )}
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Política de cancelamento</h2>

                <div className="mt-4 rounded-2xl border border-rose-100 bg-white p-5">
                  <p className="text-sm font-bold text-slate-950">
                    {payload.cancellationPolicy?.badge ?? 'Moderado'}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-700">
                    {payload.cancellationPolicy?.detalhe ??
                      'Confira as condições de cancelamento antes de reservar.'}
                  </p>
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Experiência</h2>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {Object.entries(payload.experience)
                    .filter(([key, value]) => key !== 'tempoConvivenciaPets' && Boolean(value))
                    .map(([key]) => (
                      <div
                        key={key}
                        className="rounded-xl border border-rose-100 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                      >
                        {experienceLabels[key] ?? key}
                      </div>
                    ))}

                  {payload.experience?.tempoConvivenciaPets && (
                    <div className="rounded-xl border border-rose-100 bg-white px-4 py-3 text-sm font-semibold text-slate-800">
                      Tempo de convivência com pets: {payload.experience.tempoConvivenciaPets}
                    </div>
                  )}
                </div>
              </section>

              {payload.gallery?.length > 0 && (
              <section className="mt-8">
                <h2 className="text-xl font-bold text-slate-950">Fotos do espaço</h2>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {payload.gallery.map((imageUrl: string) => (
                    <img
                      key={imageUrl}
                      src={imageUrl}
                      alt="Foto do espaço do anfitrião"
                      className="h-48 w-full rounded-2xl object-cover"
                    />
                  ))}
                </div>
              </section>
            )}

              <section className="rounded-2xl bg-success-light/30 p-5 ring-1 ring-success/10">
                <h2 className="font-bold text-slate-950">
                  Reserve com segurança pela ALLEPETS
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-700">
                  A reserva, comunicação e acompanhamento acontecem dentro da
                  plataforma, trazendo mais segurança para tutores e anfitriões.
                </p>
              </section>
            </div>
          </section>

          <aside className="space-y-6">
            <section className="hidden rounded-3xl bg-white p-6 shadow-sm ring-1 ring-brand/10 lg:block">
              <h2 className="text-lg font-bold text-slate-950">Conhecer antes de reservar</h2>
              <p className="mt-2 text-sm text-slate-600">Agende gratuitamente um encontro de apresentação, sem compromisso.</p>
              <button type="button" onClick={() => window.__AUTH_ID__ ? setAgendarAberto(true) : window.location.assign('/login?redirect=' + encodeURIComponent(window.location.pathname))} className="mt-4 w-full rounded-xl bg-green-700 p-4 font-bold text-white">Agendar encontro gratuito</button>
            </section>
            <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-brand/10">
              <span className="inline-flex rounded-full bg-warm/40 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand">
                Atendimento
              </span>

              <h2 className="mt-4 text-lg font-bold text-slate-950">
                Converse com o anfitrião
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                Abra o chat para tirar dúvidas, alinhar disponibilidade e seguir
                com a reserva com mais contexto.
              </p>

              <a
                href={chatHref}
                onClick={trackReservationClick}
                className="mt-5 block rounded-2xl bg-success px-5 py-4 text-center font-bold text-white shadow-sm transition hover:opacity-90"
              >
                Converse com o anfitrião
              </a>
            </section>

            <section className="rounded-3xl bg-white p-6 text-center shadow-sm ring-1 ring-brand/10">
              <h2 className="text-lg font-bold text-slate-950">
                Divulgue este perfil
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                Escaneie o QR Code para abrir este perfil público.
              </p>

              <div className="mx-auto mt-5 w-fit rounded-3xl bg-[#FFF8F6] p-4 ring-1 ring-brand/10">
                <img
                  src={host.qrCodeUrl}
                  alt="QR Code do perfil"
                  className="h-48 w-48 rounded-2xl bg-white p-3"
                />
              </div>

              <a
                href={host.qrCodeUrl}
                download
                className="mt-5 inline-flex rounded-xl border border-brand px-4 py-2 text-sm font-bold text-brand transition hover:bg-brand hover:text-white"
              >
                Baixar QR Code
              </a>
            </section>
          </aside>
        </div>
      </section>

      <AgendarEncontroModal open={agendarAberto} onClose={() => setAgendarAberto(false)} services={host.services} hostName={host.name} />
      <div className="fixed inset-x-0 bottom-0 border-t border-brand/10 bg-white p-4 shadow-lg lg:hidden">
        <a
          href={chatHref}
          onClick={trackReservationClick}
          className="block rounded-2xl bg-success px-5 py-4 text-center font-bold text-white shadow-sm"
        >
          Converse com o anfitrião
        </a>
      </div>
    </main>
  );
}

const container = document.getElementById('host-public-profile-root');

if (container) {
  createRoot(container).render(<HostPublicProfile />);
}
